import Pficon from 'components/Pficon';
import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Table } from 'antd';
import { DIRECTION } from 'containers/App/constants';
import Concept from './Concept';
import Attribute from './Attribute';
import AttributeValue from './ConceptAttributeValue';

function ConceptsTable({ refId, isActive, attributes, concepts }) {
  const scrollHorizontally = ({ distance, direction = DIRECTION.RIGHT }) => {
    const tbody = document.querySelector(
      '#js-concepts-table div.ant-table-body',
    );
    const tr = tbody.querySelector('tr');
    const trMeasures = tr.getBoundingClientRect();
    const tbodyLeft = tbody.getBoundingClientRect().left;
    const trLeft = trMeasures.left;

    const calcDistance = distance || (trMeasures.width - 150) / concepts.length; // 150 is the length of the first fixed column

    tbody.scrollTo({
      left:
        tbodyLeft -
        trLeft +
        (direction === DIRECTION.RIGHT ? calcDistance : -calcDistance),
      behavior: 'smooth',
    });
  };

  const [conceptsCount, setConceptsCount] = useState(0);

  useEffect(() => {
    if (conceptsCount < concepts.length) {
      scrollHorizontally({
        distance: document
          .querySelector('#js-concepts-table div.ant-table-body tr')
          .getBoundingClientRect().width,
      });
    }

    if (conceptsCount !== concepts.length) {
      setConceptsCount(concepts.length);
    }
  }, [concepts]);

  const { columnsData, dataSource } = useMemo(() => {
    const columns = [
      {
        width: 150,
        dataIndex: 'attribute',
        key: 'attribute',
        fixed: 'left',
        onHeaderCell: () => ({
          className:
            'padding-none force-text-weight-regular text-size-secondary force-border-right-grey-4 border-bottom-grey-4 force-background-grey-1 padding-sm-important',
          style: {
            height: 54,
          },
        }),
        onCell: (record, rowIndex) => ({
          className: classNames(
            'border-right-grey-4 padding-top-sm-important padding-left-sm-important padding-btm-sm-important padding-right-none-important',
            {
              'force-border-bottom-grey-4': rowIndex !== attributes.length - 1,
              'force-border-bottom-transparent':
                rowIndex === attributes.length - 1,
            },
          ),
        }),
        render: (attribute) => <Attribute attribute={attribute} />,
      },
    ];

    concepts.forEach((concept, conceptIndex) => {
      columns.push({
        key: concept.refId,
        dataIndex: `concept-${concept.refId}`,
        onHeaderCell: () => ({
          className: classNames(
            'force-text-weight-regular position-relative force-border-bottom-grey-4 force-background-grey-1 padding-sm-important',
            {
              'force-border-right-grey-4': conceptIndex !== concepts.length - 1,
              'force-border-right-none': conceptIndex === concepts.length - 1,
            },
          ),
        }),
        onCell: (record, rowIndex) => ({
          className: classNames('position-relative padding-sm-important', {
            'force-border-right-grey-4': conceptIndex !== concepts.length - 1,
            'force-border-bottom-grey-4': rowIndex !== attributes.length - 1,
            'force-border-bottom-transparent':
              rowIndex === attributes.length - 1,
          }),
        }),
        width: concepts.length <= 3 ? `${100 / concepts.length}%` : 180,
        title: (
          <Concept
            key={concept.refId}
            refId={refId}
            concept={concept}
            conceptIndex={conceptIndex}
          />
        ),
        render: (attributeValue) => (
          <AttributeValue key={attributeValue.refId} value={attributeValue} />
        ),
      });
    });

    const data = [];

    attributes.forEach((attribute, index) => {
      const dataObject = {
        key: attribute.refId,
        attribute: {
          refId: attribute.refId,
          name: attribute.name,
          index,
        },
      };

      attribute.values.forEach((attributeValue, attributeValueIndex) => {
        dataObject[`concept-${concepts[attributeValueIndex].refId}`] = {
          ...attributeValue,
          attributeRefId: attribute.refId,
          attributeName: attribute.name,
        };
      });

      data.push(dataObject);
    });

    return {
      columnsData: columns,
      dataSource: data,
    };
  }, [concepts, attributes, isActive]);

  return (
    <div className="position-relative">
      {concepts.length > 3 && (
        <div
          className="position-absolute"
          style={{ top: -24, right: 5, zIndex: 5 }}
        >
          <button
            className="cursor-pointer padding-none"
            type="button"
            onClick={() => scrollHorizontally({ direction: DIRECTION.LEFT })}
          >
            <Pficon type="keyboard_arrow_left" className="fs-16" />
          </button>
          <button
            className="cursor-pointer padding-none"
            type="button"
            onClick={() => scrollHorizontally({ direction: DIRECTION.RIGHT })}
          >
            <Pficon type="keyboard_arrow_right" className="fs-16" />
          </button>
        </div>
      )}
      <Table
        id="js-concepts-table"
        className="margin-btm-xs overflow-hidden background-grey-1 border-top-grey-4 border-bottom-grey-4"
        rowClassName="no-hover-effects"
        columns={columnsData}
        dataSource={dataSource}
        pagination={false}
        scroll={{ x: true }}
        sticky
      />
    </div>
  );
}

ConceptsTable.propTypes = {
  refId: PropTypes.string,
  isActive: PropTypes.bool,
  attributes: PropTypes.array,
  concepts: PropTypes.array,
};

export default ConceptsTable;
